import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchResource } from '@app/src/api/fetchHooks'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { Expectation, ExpectationAutomation, ExpectationWithHistories } from '@app/src/types/expectations'
import { ExpectationStatus } from '@app/src/types/expectations'

export const useFetchExpectationWithHistory = (expectationId: number | undefined) => {
  const result = useFetchCollectionWithPost<ExpectationWithHistories>({
    endpoint: endpoints.expectationCollection,
    key: [FetchKey.ExpectationCollection, expectationId, 'withHistory'],
    payload: {
      filter: [
        {
          name: 'id',
          filters: [
            {
              operator: Operators.EqualTo,
              value: expectationId,
            },
          ],
        },
      ],
      include: [
        'assessmentTemplate',
        'histories',
        'histories.creatorUser',
        'improvements',
        'importantQuestions',
        'creatorOrganization',
        'creatorOrganization.image',
        'provider',
      ],
      pagination: { pageNumber: 1, itemsPerPage: 1 },
    },
    options: {
      enabled: !!expectationId,
    },
  })

  return {
    ...result,
    data: result.items?.[0],
    histories: result.items?.[0]?.histories || [],
  }
}

export const useFetchRequestedBaselineExpectations = (enabled = true) => {
  return useFetchCollectionWithPost<Expectation>({
    key: [FetchKey.ExpectationCollection, 'uncompletedBaseline'],
    endpoint: endpoints.expectationCollection,
    shouldShowErrorNotification: false,
    payload: {
      filter: [
        {
          name: 'status',
          filters: [
            {
              value: [ExpectationStatus.Requested],
              operator: Operators.In,
            },
          ],
        },
      ],
      pagination: {
        pageNumber: 1,
        itemsPerPage: 99999,
      },
      include: ['assessmentTemplate'],
    },
    options: { enabled },
  })
}

export const useFetchInProgressExpectations = (enabled = true) => {
  return useFetchCollectionWithPost<ExpectationWithHistories>({
    key: [FetchKey.ExpectationCollection, 'inProgress'],
    endpoint: endpoints.expectationCollection,
    shouldShowErrorNotification: false,
    payload: {
      filter: [
        {
          name: 'status',
          filters: [
            {
              value: [ExpectationStatus.Accepted, ExpectationStatus.Delayed],
              operator: Operators.In,
            },
          ],
        },
      ],
      pagination: {
        pageNumber: 1,
        itemsPerPage: 99999,
      },
      include: ['histories', 'creatorOrganization', 'creatorOrganization.image', 'assessmentTemplate'],
    },
    options: { enabled },
  })
}

export const useFetchExpectationAutomations = () => {
  return useFetchCollectionWithPost<ExpectationAutomation>({
    key: FetchKey.ExpectationAutomation,
    endpoint: endpoints.expectationAutomationFilter,
    payload: {
      filter: [],
      include: ['assessmentTemplate'],
    },
  })
}

export const useFetchExpectationAutomation = (automationId: number | undefined) => {
  return useFetchResource<ExpectationAutomation>({
    key: [FetchKey.ExpectationAutomation, automationId],
    endpoint: automationId ? endpoints.expectationAutomationById(automationId) : '',
    options: {
      enabled: !!automationId,
    },
  })
}
